/**
 * Enterprise Section API - Section Fetching Module
 *
 * Extends EnterpriseAPIClient to provide unified section operations:
 * - Fetch sections by ID
 * - Fetch product sections with variant data
 * - Fetch search results
 * - Fetch product recommendations
 * - Fetch pickup availability
 * - HTML parsing and DOM updates
 *
 * Part of Zanova Enterprise Architecture - Skill #2
 *
 * REPLACES: Scattered section fetching logic across:
 * - quick-add.js
 * - quick-order-list.js
 * - quick-add-bulk.js
 * - product-info.js
 * - pickup-availability.js
 * - predictive-search.js
 * - global.js
 */

class EnterpriseSectionAPI extends EnterpriseAPIClient {
  constructor(config = {}) {
    super(config);

    // Section-specific configuration
    this.sectionConfig = {
      parseHTML: config.parseHTML !== false, // true by default
      cacheEnabled: config.cacheEnabled !== false, // true by default
      ...config
    };

    this.log('EnterpriseSectionAPI initialized', this.sectionConfig);
  }

  /**
   * Fetch single section by ID
   *
   * @param {string} sectionId - Section ID to fetch
   * @param {object} params - URL parameters
   * @param {object} options - Additional options
   * @returns {Promise} - Section HTML or parsed data
   */
  async fetchSection(sectionId, params = {}, options = {}) {
    const { parseHTML = this.sectionConfig.parseHTML, ...otherOptions } = options;

    this.log('Fetching section', { sectionId, params });

    // Build URL with parameters
    const url = this._buildSectionUrl(sectionId, params);

    try {
      const response = await this.get(url, {
        ...otherOptions,
        cache: this.sectionConfig.cacheEnabled
      });

      // Parse HTML if requested
      if (parseHTML && typeof response === 'string') {
        return this._parseHTML(response);
      }

      return response;

    } catch (error) {
      this.log('Failed to fetch section', error);
      throw error;
    }
  }

  /**
   * Fetch multiple sections at once
   *
   * @param {Array} sectionIds - Array of section IDs
   * @param {object} params - Shared URL parameters
   * @param {object} options - Additional options
   * @returns {Promise} - Object mapping section IDs to data
   */
  async fetchSections(sectionIds, params = {}, options = {}) {
    this.log('Fetching multiple sections', { count: sectionIds.length });

    const promises = sectionIds.map(sectionId =>
      this.fetchSection(sectionId, params, options)
        .then(data => ({ sectionId, data, success: true }))
        .catch(error => ({ sectionId, error, success: false }))
    );

    const results = await Promise.all(promises);

    // Convert to object mapping
    const sectionsMap = {};
    results.forEach(result => {
      if (result.success) {
        sectionsMap[result.sectionId] = result.data;
      } else {
        this.log(`Failed to fetch section ${result.sectionId}`, result.error);
      }
    });

    return sectionsMap;
  }

  /**
   * Fetch product section with variant/option data
   *
   * @param {string} productUrl - Product URL (e.g., '/products/my-product')
   * @param {object} options - Fetch options
   * @returns {Promise} - Product section HTML/data
   *
   * Options:
   * - sectionId: Section ID to fetch (default: no section filter)
   * - variantId: Specific variant ID
   * - optionValues: Array of selected option values
   * - fullPage: Fetch full page instead of section
   */
  async fetchProductSection(productUrl, options = {}) {
    const {
      sectionId = null,
      variantId = null,
      optionValues = null,
      fullPage = false,
      parseHTML = this.sectionConfig.parseHTML,
      ...otherOptions
    } = options;

    this.log('Fetching product section', { productUrl, sectionId, variantId });

    // Build parameters
    const params = {};
    if (sectionId && !fullPage) params.section_id = sectionId;
    if (variantId) params.variant = variantId;
    if (optionValues && optionValues.length) {
      params.option_values = Array.isArray(optionValues)
        ? optionValues.join(',')
        : optionValues;
    }

    // Build URL
    const url = this._buildUrl(productUrl, params);

    try {
      const response = await this.get(url, {
        ...otherOptions,
        cache: this.sectionConfig.cacheEnabled
      });

      if (parseHTML && typeof response === 'string') {
        return this._parseHTML(response);
      }

      return response;

    } catch (error) {
      this.log('Failed to fetch product section', error);
      throw error;
    }
  }

  /**
   * Fetch pickup availability for variant
   *
   * @param {string} variantId - Variant ID
   * @param {string} rootUrl - Store root URL
   * @param {object} options - Additional options
   * @returns {Promise} - Pickup availability data
   */
  async fetchPickupAvailability(variantId, rootUrl = '', options = {}) {
    if (!variantId) {
      throw new Error('Variant ID is required for pickup availability');
    }

    this.log('Fetching pickup availability', { variantId });

    const cleanRootUrl = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    const url = `${cleanRootUrl}variants/${variantId}/?section_id=pickup-availability`;

    try {
      const response = await this.get(url, {
        ...options,
        cache: true
      });

      if (typeof response === 'string') {
        const html = this._parseHTML(response);
        const section = html.querySelector('.shopify-section');
        return section;
      }

      return response;

    } catch (error) {
      this.log('Failed to fetch pickup availability', error);
      throw error;
    }
  }

  /**
   * Fetch product recommendations
   *
   * @param {string} productId - Product ID
   * @param {object} options - Fetch options
   * @returns {Promise} - Recommendations HTML/data
   *
   * Options:
   * - limit: Number of recommendations (default: 4)
   * - sectionId: Section ID for rendering
   */
  async fetchRecommendations(productId, options = {}) {
    const {
      limit = 4,
      sectionId = 'product-recommendations',
      parseHTML = this.sectionConfig.parseHTML,
      ...otherOptions
    } = options;

    if (!productId) {
      throw new Error('Product ID is required for recommendations');
    }

    this.log('Fetching product recommendations', { productId, limit });

    const url = `/recommendations/products?product_id=${productId}&limit=${limit}&section_id=${sectionId}`;

    try {
      const response = await this.get(url, {
        ...otherOptions,
        cache: true
      });

      if (parseHTML && typeof response === 'string') {
        return this._parseHTML(response);
      }

      return response;

    } catch (error) {
      this.log('Failed to fetch recommendations', error);
      throw error;
    }
  }

  /**
   * Fetch predictive search results
   *
   * @param {string} query - Search query
   * @param {object} options - Search options
   * @returns {Promise} - Search results HTML/data
   *
   * Options:
   * - sectionId: Section ID for rendering (default: 'predictive-search')
   */
  async fetchSearchResults(query, options = {}) {
    const {
      sectionId = 'predictive-search',
      parseHTML = this.sectionConfig.parseHTML,
      signal = null,
      ...otherOptions
    } = options;

    if (!query || query.trim().length === 0) {
      throw new Error('Search query is required');
    }

    this.log('Fetching search results', { query, sectionId });

    // Use global routes object if available
    const searchUrl = typeof routes !== 'undefined' && routes.predictive_search_url
      ? routes.predictive_search_url
      : '/search/suggest';

    const url = `${searchUrl}?q=${encodeURIComponent(query)}&section_id=${sectionId}`;

    try {
      const response = await this.get(url, {
        ...otherOptions,
        signal,
        cache: false // Don't cache search results
      });

      if (parseHTML && typeof response === 'string') {
        const html = this._parseHTML(response);
        const section = html.querySelector('#shopify-section-predictive-search');
        return section;
      }

      return response;

    } catch (error) {
      // Don't log aborted search requests (user is still typing)
      if (error.name !== 'AbortError') {
        this.log('Failed to fetch search results', error);
      }
      throw error;
    }
  }

  /**
   * Fetch cart drawer section
   *
   * @param {object} options - Additional options
   * @returns {Promise} - Cart drawer HTML
   */
  async fetchCartDrawer(options = {}) {
    this.log('Fetching cart drawer');

    return this.fetchSection('cart-drawer', {}, {
      ...options,
      parseHTML: true
    });
  }

  /**
   * UTILITY METHODS
   */

  /**
   * Build section URL with parameters
   */
  _buildSectionUrl(sectionId, params = {}) {
    const baseUrl = `?section_id=${sectionId}`;
    const queryParams = new URLSearchParams(params);

    if (queryParams.toString()) {
      return `${baseUrl}&${queryParams.toString()}`;
    }

    return baseUrl;
  }

  /**
   * Build URL with query parameters
   */
  _buildUrl(baseUrl, params = {}) {
    if (!baseUrl) return '';

    const queryParams = new URLSearchParams(params);
    const separator = baseUrl.includes('?') ? '&' : '?';

    if (queryParams.toString()) {
      return `${baseUrl}${separator}${queryParams.toString()}`;
    }

    return baseUrl;
  }

  /**
   * Parse HTML string to DOM
   */
  _parseHTML(htmlString) {
    if (typeof DOMParser === 'undefined') {
      window.debugLogger.error('DOMParser not available');
      return htmlString;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    return doc;
  }

  /**
   * Extract section inner HTML from parsed document
   *
   * @param {Document} parsedDoc - Parsed HTML document
   * @param {string} selector - CSS selector for section
   * @returns {string} - Inner HTML
   */
  extractSectionHTML(parsedDoc, selector) {
    if (!parsedDoc || typeof parsedDoc.querySelector !== 'function') {
      this.log('Invalid parsed document', parsedDoc);
      return '';
    }

    const element = parsedDoc.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  /**
   * Update DOM element with section HTML
   *
   * @param {HTMLElement} targetElement - Element to update
   * @param {string} sectionHTML - HTML to insert
   * @returns {boolean} - Success status
   */
  updateElement(targetElement, sectionHTML) {
    if (!targetElement) {
      this.log('Target element not found');
      return false;
    }

    targetElement.innerHTML = sectionHTML;
    this.log('Updated DOM element', targetElement.id || targetElement.className);
    return true;
  }
}

// Export for use in theme
if (typeof window !== 'undefined') {
  window.EnterpriseSectionAPI = EnterpriseSectionAPI;

  // Create global section API instance
  window.sectionAPI = new EnterpriseSectionAPI({
    enableLogging: false // Disabled in production
  });
}
